/**
 * Minimal shape needed by the date-gate logic. The full `ReviewItem`
 * type (from `@repo/supabase-client` generated types) has many more
 * fields; expose only what we read here so the helper is reusable
 * across components without coupling to the full row shape.
 */
export interface ReviewItemForDateGate {
  id: string;
  /**
   * review_items.type — runtime values are 'task' | 'event', but the
   * Supabase-generated Database type widens to `string`. Keeping this
   * `string` lets the helper accept the full `ReviewItem` row without
   * a cast at every call site; the predicate compares to literal
   * 'task' anyway, so the narrower type bought us nothing at runtime.
   */
  type: string;
  /** review_items.status — 'pending' | 'committed' | 'dismissed' (typed loose for forward compat). */
  status: string;
  /** review_items.due_datetime — ISO string when set; null when the LLM extracted no date (TASK-specific). */
  due_datetime: string | null;
  /** review_items.start_datetime — ISO string when set; null when the LLM extracted no date (EVENT-specific). */
  start_datetime: string | null;
}

/**
 * Return the subset of `items` that block an email-level "Approve" —
 * tasks the user hasn't dismissed yet that still have no due date.
 *
 * Events are intentionally OUT of scope: jobs-process-emails fills a
 * +15-minute default end_datetime when the LLM omits one, and date-only
 * events ("Tuesday") legitimately keep null end. The Approve gate is
 * specifically about tasks the user needs to date before they can land
 * on Today / Schedule.
 */
export function tasksNeedingDate<T extends ReviewItemForDateGate>(
  items: T[],
): T[] {
  return items.filter(
    (i) =>
      i.type === 'task' &&
      i.status !== 'dismissed' &&
      i.status !== 'committed' &&
      !i.due_datetime,
  );
}

/**
 * Return the subset of `items` that BLOCK confirmation — events the user
 * hasn't dismissed yet that still have no start date.
 *
 * Unlike undated tasks (which are allowed through a confirmation modal and
 * land in Today → Unscheduled), an undated event has nowhere to go on the
 * calendar, so confirmation is hard-blocked until the user dates it. See
 * the Path C flow in the confirm-undated-paths spec.
 */
export function eventsNeedingDate<T extends ReviewItemForDateGate>(
  items: T[],
): T[] {
  return items.filter(
    (i) =>
      i.type === 'event' &&
      i.status !== 'dismissed' &&
      i.status !== 'committed' &&
      !i.start_datetime,
  );
}

/**
 * Discriminated state for the Review "Confirm" CTA. Encodes the
 * date-completeness precedence the footer needs:
 *
 * - `blocked-events` (Path C): one or more events lack a date → the CTA is
 *   disabled and an amber warning is shown. Events win over tasks because
 *   they can't be confirmed without a date at all.
 * - `needs-task-confirmation` (Path B): no undated events, but one or more
 *   tasks lack a date → the CTA stays active and opens the
 *   "Confirm without a date?" modal on click.
 * - `ready` (Path A): everything is dated → confirm directly.
 */
export type ReviewConfirmGate<T extends ReviewItemForDateGate> =
  | { kind: 'blocked-events'; events: T[] }
  | { kind: 'needs-task-confirmation'; tasks: T[] }
  | { kind: 'ready' };

export function reviewConfirmGate<T extends ReviewItemForDateGate>(
  items: T[],
): ReviewConfirmGate<T> {
  const events = eventsNeedingDate(items);
  if (events.length > 0) return { kind: 'blocked-events', events };
  const tasks = tasksNeedingDate(items);
  if (tasks.length > 0) return { kind: 'needs-task-confirmation', tasks };
  return { kind: 'ready' };
}
